import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import {
  Box,
  Typography,
  // MenuItem,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  Fade,
  Grid,
} from "@mui/material";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import { toast } from "react-toastify";
import { signUp } from "../actions/userActions";
import { phone } from "phone";
import SignUpSuccessDialog from "../dialogs/SignUpSuccessDialog";
// import CorporateDialog from "../dialogs/CorporateDialog";
import TermsDialog from "../dialogs/TermsDialog";
import PrivacyPolicyDialog from "../dialogs/PrivacyPolicyDialog";
import {
  ModernTextField,
  ModernButton,
  GradientBackground,
  AuthCard,
} from "../components/UI/ModernComponents";

export default function SignUpForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userSignup = useSelector((state) => state.userSignup);
  const { loading, message, errorMessage, status } = userSignup;

  const [isDialogOpen, setDialogOpen] = useState(false);
  // const [isCorporateDialogOpen, setCorporateDialogOpen] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const [openPrivacyPolicyDialog, setOpenPrivacyPolicyDialog] = useState(false);

  const validatePhoneNumber = (inputNumber) => {
    if (!inputNumber) return "Phone Number is required";
    const { isValid } = phone(inputNumber, { country: "pk" });
    return isValid ? true : "Invalid phone number format";
  };

  const validateCNIC = (inputCnic) => {
    if (!inputCnic) return "CNIC is required";
    const cnicRegex = /^(\d{13}|\d{5}-\d{7}-\d{1})$/;
    return (
      cnicRegex.test(inputCnic) || "Invalid CNIC format (e.g., 35202-1234567-1)"
    );
  };

  const formatCNIC = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 13);
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
  };

  const {
    register,
    handleSubmit,
    setError,
    watch,
    control,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const password = watch("password", "");

  const onSubmit = async (data) => {
    const { fullName, email, phoneNumber, cnic, password } = data;
    try {
      dispatch(
        signUp(
          fullName,
          email,
          phoneNumber,
          password,
          cnic,
          // userType,
          // organization,
          // designation,
          // officeAddress,
          // barCouncil
        ),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const showPopup = () => {
    setDialogOpen(true);
  };

  const closePopup = () => {
    setDialogOpen(false);
    navigate("/");
  };

  // const showCorporatePopup = () => {
  //   setCorporateDialogOpen(true);
  // };

  // const closeCorporatePopup = () => {
  //   setCorporateDialogOpen(false);
  //   navigate("/");
  // };

  useEffect(() => {
    if (errorMessage) {
      if (errorMessage === "Email already exists.") {
        setError("email", {
          type: "manual",
          message: "Email already exists.",
        });
      } else if (errorMessage === "Phone number already exists.") {
        setError("phoneNumber", {
          type: "manual",
          message: "Phone number already exists.",
        });
      } else if (errorMessage === "CNIC already exists.") {
        setError("cnic", {
          type: "manual",
          message: "CNIC already exists.",
        });
      }
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
      dispatch({ type: "USER_SIGNUP_RESET" });
    }
    if (message) {
      if (status === 200) {
        // showCorporatePopup();
        showPopup();
      } else {
        showPopup();
      }
      dispatch({ type: "USER_SIGNUP_RESET" });
    }
  }, [message, errorMessage, status, dispatch, setError]);

  return (
    <>
      <Helmet>
        <title>LawGPT - Create Account</title>
      </Helmet>
      <GradientBackground
        sx={{
          py: { xs: 4, sm: 6 },
          px: 2,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Render sign-up card directly without corporate dialog ternary */}
        <Fade in timeout={800}>
          <AuthCard
            elevation={0}
            sx={{
              maxWidth: { xs: "100%", sm: "640px", md: "720px" },
              width: "100%",
              p: { xs: 2.5, sm: 4 },
              mx: "auto",
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              {/* Logo */}
              <Box sx={{ mb: 1.5, display: "flex", justifyContent: "center" }}>
                <img
                  src='/images/legalSearchLogo2.png'
                  alt='LawGPT Logo'
                  style={{
                    width: "160px",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "22px", sm: "26px" },
                  color: "#12243C",
                  mb: 0.5,
                  fontFamily: "var(--font-sans)",
                  background:
                    "linear-gradient(135deg, #12243C 0%, #0073A6 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Create your account
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: "#6B7280",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "var(--font-sans)",
                }}
              >
                Join LawGPT and transform your legal practice
              </Typography>
            </Box>

            {/* Form */}
            <Box component='form' onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {/* Full Name */}
                <Grid item xs={12} sm={6}>
                  <ModernTextField
                    fullWidth
                    label='Full Name'
                    variant='outlined'
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <PersonIcon
                            sx={{ color: "#9CA3AF", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    {...register("fullName", {
                      required: "Full name is required",
                      minLength: {
                        value: 2,
                        message: "Name must be at least 2 characters",
                      },
                    })}
                    error={!!errors.fullName}
                    helperText={errors.fullName?.message}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "12px",
                        marginLeft: "4px",
                        color: "#EF4444",
                        fontFamily:
                          "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                      },
                    }}
                  />
                </Grid>

                {/* Email Address */}
                <Grid item xs={12} sm={6}>
                  <ModernTextField
                    fullWidth
                    label='Email Address'
                    variant='outlined'
                    type='email'
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <EmailOutlinedIcon
                            sx={{ color: "#9CA3AF", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "12px",
                        marginLeft: "4px",
                        color: "#EF4444",
                        fontFamily:
                          "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                      },
                    }}
                  />
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12} sm={6}>
                  <ModernTextField
                    fullWidth
                    label='Phone Number'
                    variant='outlined'
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <PhoneIcon
                            sx={{ color: "#9CA3AF", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    {...register("phoneNumber", {
                      required: "Phone number is required",
                      validate: validatePhoneNumber,
                    })}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "12px",
                        marginLeft: "4px",
                        color: "#EF4444",
                        fontFamily:
                          "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                      },
                    }}
                  />
                </Grid>

                {/* CNIC Number */}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name='cnic'
                    control={control}
                    rules={{
                      required: "CNIC is required",
                      validate: validateCNIC,
                    }}
                    render={({ field: { onChange, value, ref } }) => (
                      <ModernTextField
                        fullWidth
                        inputRef={ref}
                        value={value || ""}
                        label='CNIC Number'
                        placeholder='35202-1234567-1'
                        variant='outlined'
                        inputProps={{ maxLength: 15 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <BadgeOutlinedIcon
                                sx={{ color: "#9CA3AF", fontSize: "20px" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        onKeyDown={(e) => {
                          const allowedKeys = [
                            "Backspace",
                            "Delete",
                            "Tab",
                            "Escape",
                            "Enter",
                            "ArrowLeft",
                            "ArrowRight",
                            "Home",
                            "End",
                          ];
                          if (
                            allowedKeys.includes(e.key) ||
                            e.ctrlKey ||
                            e.metaKey
                          )
                            return;
                          if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => {
                          const formatted = formatCNIC(e.target.value);
                          onChange(formatted); // Keeps RHF state & UI perfectly synchronized
                        }}
                        error={!!errors.cnic}
                        helperText={errors.cnic?.message}
                        FormHelperTextProps={{
                          sx: {
                            fontSize: "12px",
                            marginLeft: "4px",
                            color: "#EF4444",
                            fontFamily:
                              "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                {/* Password */}
                <Grid item xs={12} sm={6}>
                  <ModernTextField
                    fullWidth
                    label='Password'
                    variant='outlined'
                    type={showPassword ? "text" : "password"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <LockOutlinedIcon
                            sx={{ color: "#9CA3AF", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge='end'
                            sx={{
                              color: "#9CA3AF",
                              padding: "4px",
                              "&:hover": {
                                color: "#6B7280",
                              },
                            }}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlined />
                            ) : (
                              <VisibilityOutlined />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 8,
                        message: "Password must be at least 8 characters",
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message:
                          "Password must contain uppercase, lowercase, and number",
                      },
                    })}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "12px",
                        marginLeft: "4px",
                        color: "#EF4444",
                        fontFamily:
                          "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                      },
                    }}
                  />
                </Grid>

                {/* Confirm Password */}
                <Grid item xs={12} sm={6}>
                  <ModernTextField
                    fullWidth
                    label='Confirm Password'
                    variant='outlined'
                    type={showConfirmPassword ? "text" : "password"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <LockOutlinedIcon
                            sx={{ color: "#9CA3AF", fontSize: "20px" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge='end'
                            sx={{
                              color: "#9CA3AF",
                              padding: "4px",
                              "&:hover": {
                                color: "#6B7280",
                              },
                            }}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOffOutlined />
                            ) : (
                              <VisibilityOutlined />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register("confirmPassword", {
                      required: "Please confirm your password",
                      validate: (value) =>
                        value === password || "Passwords do not match",
                    })}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "12px",
                        marginLeft: "4px",
                        color: "#EF4444",
                        fontFamily:
                          "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                      },
                    }}
                  />
                </Grid>

                {/* Terms and Privacy */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...register("terms", {
                            required:
                              "You must accept the terms and conditions",
                          })}
                          sx={{
                            color: "#0073A6",
                            "&.Mui-checked": {
                              color: "#00C4F0",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant='body2'
                          sx={{
                            color: "#6B7280",
                            fontSize: "13px",
                            fontFamily: "var(--font-sans)",
                          }}
                        >
                          I agree to the{" "}
                          <Typography
                            component='span'
                            onClick={() => setOpenTermsDialog(true)}
                            sx={{
                              cursor: "pointer",
                              color: "#0073A6",
                              fontWeight: 600,
                              fontSize: "13px",
                              fontFamily: "var(--font-sans)",
                              "&:hover": {
                                color: "#00C4F0",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Terms of Service
                          </Typography>{" "}
                          and{" "}
                          <Typography
                            component='span'
                            onClick={() => setOpenPrivacyPolicyDialog(true)}
                            sx={{
                              cursor: "pointer",
                              color: "#0073A6",
                              fontWeight: 600,
                              fontSize: "13px",
                              fontFamily: "var(--font-sans)",
                              "&:hover": {
                                color: "#00C4F0",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Privacy Policy
                          </Typography>
                        </Typography>
                      }
                    />
                    {errors.terms && (
                      <Typography
                        variant='caption'
                        sx={{
                          color: "#EF4444",
                          fontSize: "12px",
                          ml: 4,
                          display: "block",
                          fontFamily: "var(--font-sans)",
                        }}
                      >
                        {errors.terms.message}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Sign Up Button */}
                <Grid item xs={12}>
                  <ModernButton
                    fullWidth
                    variant='contained'
                    type='submit'
                    disabled={loading}
                    sx={{
                      backgroundColor: "#0073A6",
                      py: 1.2,
                      "&:hover": {
                        backgroundColor: "#005A8A",
                      },
                      "&:disabled": {
                        backgroundColor: "#E5E7EB",
                        color: "#9CA3AF",
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
                    ) : (
                      "Create Account"
                    )}
                  </ModernButton>
                </Grid>
              </Grid>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: 2.5 }}>
              <Typography
                variant='body2'
                sx={{
                  color: "#9CA3AF",
                  px: 2,
                  fontSize: "13px",
                  fontFamily: "var(--font-sans)",
                }}
              >
                or
              </Typography>
            </Divider>

            {/* Login Link */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant='body2'
                sx={{
                  color: "#6B7280",
                  fontSize: "13px",
                  fontFamily: "var(--font-sans)",
                }}
              >
                Already have an account?{" "}
                <Typography
                  component='span'
                  onClick={() => navigate("/")}
                  sx={{
                    cursor: "pointer",
                    color: "#0073A6",
                    fontWeight: 600,
                    fontSize: "13px",
                    fontFamily: "var(--font-sans)",
                    "&:hover": {
                      color: "#00C4F0",
                      textDecoration: "underline",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Sign in
                </Typography>
              </Typography>
            </Box>
          </AuthCard>
        </Fade>

        {/*
        <CorporateDialog
          isCorporateDialogOpen={isCorporateDialogOpen}
          closeCorporatePopup={closeCorporatePopup}
        />
        */}

        <SignUpSuccessDialog open={isDialogOpen} onClose={closePopup} />

        {/* Terms Dialog */}
        <TermsDialog
          open={openTermsDialog}
          onClose={() => setOpenTermsDialog(false)}
        />

        {/* Privacy Policy Dialog */}
        <PrivacyPolicyDialog
          open={openPrivacyPolicyDialog}
          onClose={() => setOpenPrivacyPolicyDialog(false)}
        />
      </GradientBackground>
    </>
  );
}
